import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import * as forge from 'node-forge';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { StorageService } from 'src/app/_services/storage.service';
const { USER_MANAGEMENT_URL } = environment;

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  private publicKey!: string;
  private encKey!: string;
  private readonly httpClient: HttpClient;

  constructor(private readonly httpBackend: HttpBackend, private readonly http: HttpClient, private readonly storageService: StorageService) {
    // Create HttpClient that bypasses interceptors
    this.httpClient = new HttpClient(httpBackend);
  }

  private loadPublicKey(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpClient.get(`${USER_MANAGEMENT_URL}/auth/public-key`,
      ).subscribe({
        next: (response: any) => {
          this.publicKey = response.key;
          resolve();
        },
        error: (error) => {
          console.error('Failed to load public key:', error);
          reject(new Error('Cannot encrypt/descript: Public key not yet loaded'));
        }
      });
    });
  }

  async encryptRequestBody(requestBody: any, isEncryptByPublicKey: boolean, path: string): Promise<any> {
    // console.debug(`[EncryptionService] encryptRequestBody START for path: ${path}`, requestBody);
    if (!requestBody) return requestBody;
    return await this.encrypt(requestBody, isEncryptByPublicKey, path);
    // console.debug(`[EncryptionService] encryptRequestBody END for path: ${path}`, result);
  }

  async decryptResponseBody(responseBody: any): Promise<any> {
    // console.debug('[EncryptionService] decryptResponseBody START', responseBody);
    if (!responseBody?.encData || !responseBody?.encKey) return responseBody;
    // console.debug('[EncryptionService] No encryption found, returning original response');
    return await this.decrypt(responseBody);
    // console.debug('[EncryptionService] decryptResponseBody END', result);
  }

  private async encrypt(data: any, isEncryptByPublicKey: boolean, path: string): Promise<any> {
    try {
      const jsonString = JSON.stringify(data);
      // Step 1: Generate a new AES key for this response (randomAESKey)
      const randomAESKey = this.generateAES256SymmatricKey();
      if(isEncryptByPublicKey) {
        if(!this.publicKey) { // make sure publicKey is loaded
          await this.loadPublicKey();
        }
        if(path.endsWith('/auth/encrypt/login')) {
          this.encKey = randomAESKey;
          this.storageService.setEncKey(this.encKey);
        }
        // Append randomKey to the data
        const dataWithKey = {
          ...data,
          encKey: randomAESKey
        };
        const dataWithKeyString = JSON.stringify(dataWithKey);
      // console.log(`Data with Key String`,dataWithKeyString);
        // Use node-forge for RSA/ECB/OAEPWithSHA-256AndMGF1Padding encryption
        return {encData: this.rsaEncryptWithForge(dataWithKeyString)};
      } else {
        // Use AES/GCM/NoPadding encryption to match server implementation
        // Step 2: Encrypt data with decResponseKey using AES/GCM via Web Crypto API
        const encryptedData = await this.aesGcmEncrypt(jsonString, randomAESKey);
        
        // Step 3: Encrypt decResponseKey with encKey using AES/GCM via Web Crypto API
        this.checkEncKey();
        const encryptedKey = await this.aesGcmEncrypt(randomAESKey, this.encKey);
        
        return {
          encData: encryptedData,
          encKey: encryptedKey
        };
      }
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  private checkEncKey() {
    if(!this.encKey) {
      this.encKey = this.storageService.getEncKey();
    }
  }

  private async decrypt(encryptedData: any): Promise<any> {
    try {
      // Step 1: Decrypt encKey using encKey with GCM mode
      this.checkEncKey();
      // console.debug("decrypt key aesGcmDecrypt:" + encryptedData.encKey + ", key:" + this.encKey);
      const decryptedKey = await this.aesGcmDecrypt(encryptedData.encKey, this.encKey);
      
      if (!decryptedKey) {
        throw new Error('Failed to decrypt encKey - invalid encKey or corrupted data');
      }
      
      // console.debug('Decrypted key:', decryptedKey);
      
      // Step 2: Decrypt encData using the decrypted key with GCM mode
      // console.debug("decrypt data aesGcmDecrypt:" + encryptedData.encData + ", key:" + decryptedKey);
      const decryptedString = await this.aesGcmDecrypt(encryptedData.encData, decryptedKey);
      
      if (!decryptedString) {
        throw new Error('Failed to decrypt encData - invalid key or corrupted data');
      }
      // console.debug('decrypt value: ', decryptedString);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  private generateAES256SymmatricKey(): string {
    try {
      // Generate AES-256 key (32 bytes = 256 bits) to match Java KeyGenerator.getInstance("AES").init(256)
      const secretKey = CryptoJS.lib.WordArray.random(32); // 32 bytes = 256 bits
      
      // Convert to Base64 and take first 32 characters to match Java logic:
      // Base64.getEncoder().encodeToString(secretKey.getEncoded()).substring(0, 32)
      return CryptoJS.enc.Base64.stringify(secretKey).substring(0, 32);
    } catch (error) {
      console.error('Failed to generate symmetric key:', error);
      return null as any; // Match Java's return null on exception
    }
  }

  /**
   * AES-GCM encryption using Web Crypto API to match Java server implementation
   * Java: AES/GCM/NoPadding with IV derived from key.substring(0, 16)
   */
  private async aesGcmEncrypt(data: string, key: string): Promise<string> {
    try {
      // Convert key to bytes (first 32 chars for AES-256)
      const keyBytes = new TextEncoder().encode(key.substring(0, 32));
      
      // IV is derived from key: key.substring(0, 16) - matches Java implementation
      const iv = new TextEncoder().encode(key.substring(0, 16));
      
      // Import key for Web Crypto API
      const cryptoKey = await globalThis.crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-GCM' },
        false,
        ['encrypt']
      );
      
      // Encrypt data
      const dataBytes = new TextEncoder().encode(data);
      const encrypted = await globalThis.crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: 128 // 128-bit tag to match Java TAG_LENGTH
        },
        cryptoKey,
        dataBytes
      );
      
      // Convert to base64
      return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
    } catch (error) {
      console.error('AES-GCM encryption error:', error);
      throw new Error('Failed to encrypt with AES-GCM');
    }
  }

  /**
   * AES-GCM decryption using Web Crypto API to match Java server implementation
   */
  private async aesGcmDecrypt(encryptedData: string, key: string): Promise<string> {
    try {
      // Convert key to bytes (first 32 chars for AES-256)
      const keyBytes = new TextEncoder().encode(key.substring(0, 32));
      
      // IV is derived from key: key.substring(0, 16) - matches Java implementation
      const iv = new TextEncoder().encode(key.substring(0, 16));
      
      // Import key for Web Crypto API
      const cryptoKey = await globalThis.crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-GCM' },
        false,
        ['decrypt']
      );
      
      // Convert base64 to bytes
      const encryptedBytes = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
      
      // Decrypt data
      const decrypted = await globalThis.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: 128 // 128-bit tag to match Java TAG_LENGTH
        },
        cryptoKey,
        encryptedBytes
      );
      
      // Convert back to string
      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('AES-GCM decryption error:', error);
      throw new Error('Failed to decrypt with AES-GCM');
    }
  }

  /**
   * Parse public key using direct PEM format
   */
  private parseDirectPem(): any {
    return forge.pki.publicKeyFromPem(this.publicKey);
  }

  /**
   * Parse public key by formatting it to proper PEM format
   */
  private parseFormattedPem(): any {
    let formattedKey = this.publicKey.trim();
    
    // Add PEM headers if missing
    if (!formattedKey.startsWith('-----BEGIN')) {
      formattedKey = '-----BEGIN PUBLIC KEY-----\n' + formattedKey + '\n-----END PUBLIC KEY-----';
    }
    
    // Ensure proper line breaks for PEM format
    if (!formattedKey.includes('\n')) {
      const keyContent = this.extractKeyContent(formattedKey);
      const formattedContent = keyContent.match(/.{1,64}/g)?.join('\n') || keyContent;
      formattedKey = '-----BEGIN PUBLIC KEY-----\n' + formattedContent + '\n-----END PUBLIC KEY-----';
    }
    return forge.pki.publicKeyFromPem(formattedKey);
  }

  /**
   * Parse public key using ASN.1 DER format
   */
  private parseAsn1Der(): any {
    const keyContent = this.extractKeyContent(this.publicKey);
    const keyBytes = forge.util.decode64(keyContent);
    const asn1 = forge.asn1.fromDer(keyBytes);
    return forge.pki.publicKeyFromAsn1(asn1);
  }

  /**
   * Extract key content by removing PEM headers and whitespace
   */
  private extractKeyContent(key: string): string {
    return key
      .replaceAll('-----BEGIN PUBLIC KEY-----', '')
      .replaceAll('-----END PUBLIC KEY-----', '')
      .replaceAll(/\s/g, '');
  }

  /**
   * Parse public key using multiple fallback approaches
   */
  private parsePublicKey(): any {
    const approaches = [
      { name: 'Direct PEM', method: () => this.parseDirectPem() },
      { name: 'Formatted PEM', method: () => this.parseFormattedPem() },
      { name: 'ASN.1 DER', method: () => this.parseAsn1Der() }
    ];

    const errors: { [key: string]: string } = {};

    for (const approach of approaches) {
      try {
        return approach.method();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        errors[approach.name] = errorMessage;
        console.log(`${approach.name} parsing failed, trying next approach...`);
      }
    }

    console.error('All public key parsing approaches failed:', errors);
    throw new Error('Failed to parse public key: Invalid key format. All parsing approaches failed.');
  }

  private rsaEncryptWithForge(data: string): string {
    try {
      const publicKey = this.parsePublicKey();
      
      // Encrypt using RSA/ECB/OAEPWithSHA-256AndMGF1Padding
      const encrypted = publicKey.encrypt(data, 'RSA-OAEP', {
        md: forge.md.sha256.create(),
        mgf1: {
          md: forge.md.sha256.create()
        }
      });
      
      // Convert to base64
      return forge.util.encode64(encrypted);
    } catch (error) {
      console.error('RSA encryption error:', error);
      console.error('Public key format:', this.publicKey);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error('Failed to encrypt data with RSA: ' + errorMessage);
    }
  }
}
